import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  PenSquare, Loader2, Image as ImageIcon, Video, X, Hash,
  Upload, Sparkles, Check, FileText, Layers, Send, Clock, Save, Wand2, Building2, ScrollText,
} from 'lucide-react';
import { toast } from 'sonner';
import { FunnelCtaPicker, buildTrackedUrl } from '../components/FunnelCtaPicker';
import { VideoStudioModal } from '../components/VideoStudioModal';
import { useFeature } from '../hooks/useFeatures';

// Platforms the user can target — keep aligned with backend SUPPORTED_PLATFORMS.
// Bluesky uses app passwords (no admin OAuth) so it's always available; the
// rest depend on the org owner having configured the OAuth credentials.
const PLATFORMS = [
  { key: 'linkedin',  label: 'LinkedIn',  cls: 'bg-sky-50 text-sky-700 border-sky-200' },
  { key: 'instagram', label: 'Instagram', cls: 'bg-pink-50 text-pink-700 border-pink-200' },
  { key: 'facebook',  label: 'Facebook',  cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  { key: 'youtube',   label: 'YouTube Shorts', cls: 'bg-red-50 text-red-700 border-red-200' },
  { key: 'tiktok',    label: 'TikTok',    cls: 'bg-zinc-100 text-zinc-700 border-zinc-200' },
  { key: 'bluesky',   label: 'Bluesky',   cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
];

// Format a Date as the local "YYYY-MM-DDTHH:MM" string a datetime-local input wants.
const toLocalInput = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const POST_TYPES = [  { key: 'standard',     label: 'Text / Image post', icon: FileText, hint: 'Standard caption + optional single image or video.' },
  { key: 'carousel',     label: 'Carousel',          icon: Layers,   hint: 'Multiple images (Instagram, LinkedIn). Add 2-10 images.' },
  { key: 'video_script', label: 'Video / Reel',      icon: Video,    hint: 'Single video upload (Reel for IG, Short for YouTube, etc.).' },
];

export const ComposePage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [ctaFunnel, setCtaFunnel] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  // Multi-page selection (agencies / users with >1 connection per platform).
  // targetsByPlatform: { facebook: [{account_id,account_name,kind,...}, ...] }
  // selectedTargets:   { facebook: [account_id, ...] } — pages to fan out to.
  const [targetsByPlatform, setTargetsByPlatform] = useState({});
  const [selectedTargets, setSelectedTargets] = useState({});
  const [postType, setPostType] = useState('standard');
  const [media, setMedia] = useState([]);            // [{id,type,url,filename,original_name,...}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Phase Q.1 — three publish modes: save draft, post now, schedule later.
  // Default the date+time to "1 hour from now, rounded to next 15 min" so
  // the user just picks a different time if they wanted earlier/later.
  const [mode, setMode] = useState('draft');         // 'draft' | 'now' | 'schedule'
  const [scheduledLocal, setScheduledLocal] = useState(() => {
    const ms = 15 * 60 * 1000;
    const d = new Date(Math.ceil((Date.now() + 60 * 60 * 1000) / ms) * ms);
    return toLocalInput(d);
  });
  const [minScheduled] = useState(() => toLocalInput(new Date(Date.now() + 5 * 60 * 1000)));
  // AI image generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [smartLoading, setSmartLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const videoFeature = useFeature('video_studio');

  // Load every publish destination once, grouped by platform. Lets us show a
  // page picker whenever a platform has more than one connected page/account.
  useEffect(() => {
    let mounted = true;
    api.get('/connections/targets')
      .then((r) => {
        if (!mounted) return;
        const grouped = {};
        (r.data?.targets || []).forEach((t) => {
          (grouped[t.platform] = grouped[t.platform] || []).push(t);
        });
        setTargetsByPlatform(grouped);
      })
      .catch(() => { /* connections page surfaces errors; Compose still works */ });
    return () => { mounted = false; };
  }, [api]);

  const togglePlatform = (key) => {
    setPlatforms((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]);
    // Default the page selection to the first available target for this platform.
    setSelectedTargets((prev) => {
      if (prev[key]?.length) return prev;
      const opts = targetsByPlatform[key] || [];
      return opts.length ? { ...prev, [key]: [opts[0].account_id] } : prev;
    });
  };

  const toggleTarget = (platformKey, accountId) => {
    setSelectedTargets((prev) => {
      const cur = prev[platformKey] || [];
      const next = cur.includes(accountId) ? cur.filter((x) => x !== accountId) : [...cur, accountId];
      return { ...prev, [platformKey]: next };
    });
  };

  const allTargetsSelected = (platformKey) => {
    const opts = targetsByPlatform[platformKey] || [];
    const sel = selectedTargets[platformKey] || [];
    return opts.length > 0 && sel.length === opts.length;
  };

  const toggleAllTargets = (platformKey) => {
    const opts = targetsByPlatform[platformKey] || [];
    setSelectedTargets((prev) => ({
      ...prev,
      [platformKey]: allTargetsSelected(platformKey) ? [opts[0]?.account_id].filter(Boolean) : opts.map((o) => o.account_id),
    }));
  };

  // Total number of post rows that will be created (one per selected page).
  const totalPosts = platforms.reduce((sum, p) => {
    const opts = targetsByPlatform[p] || [];
    const sel = selectedTargets[p] || [];
    if (opts.length > 1) return sum + Math.max(1, sel.length);
    return sum + 1;
  }, 0);

  const addHashtagFromInput = () => {
    const raw = hashtags.trim();
    if (!raw) return;
    // Split & normalise — accept "#tag1 tag2,tag3" any way the user types it.
    // Each token gets a single leading # and is whitespace-stripped.
    const tokens = raw.split(/[\s,]+/).filter(Boolean).map((t) => t.startsWith('#') ? t : `#${t}`);
    setHashtags(tokens.join(' '));
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await api.post('/uploads/media', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(res.data);
      }
      // Auto-flip post-type when media shape implies it.
      const next = [...media, ...uploaded];
      setMedia(next);
      if (postType === 'standard' && next.filter((m) => m.type === 'image').length >= 2) {
        setPostType('carousel');
      } else if (postType === 'standard' && uploaded.some((m) => m.type === 'video')) {
        setPostType('video_script');
      }
      toast.success(`Uploaded ${uploaded.length} file${uploaded.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeMedia = (id) => setMedia((m) => m.filter((x) => x.id !== id));

  // "Smart prompt": ask the AI to rewrite the caption into a concrete visual
  // brief (composition, subject, lighting, mood) that an image model can render
  // well. Cheaper than the user manually iterating on prompts.
  const handleSmartPrompt = async () => {
    if (!caption.trim()) { toast.error('Write a caption first'); return; }
    setSmartLoading(true);
    try {
      const platform_hint = platforms[0] || 'instagram';
      const res = await api.post('/generate/smart-image-prompt', {
        caption: caption.trim(),
        platform: platform_hint,
      });
      setAiPrompt(res.data.prompt || '');
      toast.success('Visual brief generated — review then hit Generate image.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate brief');
    } finally {
      setSmartLoading(false);
    }
  };

  // Generate an image from the (smart or hand-written) prompt and attach it.
  const handleGenerateImage = async () => {
    const prompt = (aiPrompt || caption).trim();
    if (!prompt) { toast.error('No prompt to generate from'); return; }
    setGenLoading(true);
    try {
      // POST /generate/image needs a post_id. Compose hasn't created any yet,
      // so we use the standalone /generate/image-standalone shape: backend
      // already exposes /uploads/media for direct files. Easiest path: call
      // /generate/image with no post_id by hitting a thin route that returns
      // base64, OR use the existing flow by creating a draft first.
      // For Compose we keep it simple: create a hidden draft, generate, then
      // re-fetch the media item and attach to local state.
      // NOTE: this is intentional — the auto-regen safety net on publish_now
      // is the long-term insurance. This is the foreground UX path.
      const draft = await api.post('/posts', {
        caption: 'temp', platforms: ['linkedin'], post_type: 'standard', media_items: [],
      });
      const tempId = draft.data.created_ids?.[0];
      if (!tempId) throw new Error('Could not allocate draft for image generation');
      try {
        await api.post('/generate/image', {
          post_id: tempId,
          prompt: prompt,
          model: 'nano-banana',
        });
        // Fetch the newly attached media from the temp post
        const got = await api.get(`/posts/${tempId}`);
        const newMedia = (got.data.media || []).filter(
          (m) => (m.source === 'ai_generated' || m.source === 'ai_auto' || m.source === 'ai')
            && !media.some((existing) => existing.id === m.id)
        );
        if (newMedia.length === 0) {
          toast.error('Generation did not return an image');
          return;
        }
        // Adopt it onto the Compose-page local state and let user save it
        // along with the real submission.
        setMedia((m) => [...m, ...newMedia]);
        if (postType === 'standard') {
          // If they had no media yet, we don't change post type.
        }
        toast.success('Image generated and attached.');
      } finally {
        // Always clean up the temp draft.
        api.delete(`/posts/${tempId}`).catch(() => {});
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setGenLoading(false);
    }
  };

  const validate = () => {
    if (!caption.trim()) { toast.error('Caption is required'); return false; }
    if (platforms.length === 0) { toast.error('Pick at least one platform'); return false; }
    // Multi-page platforms need at least one page chosen.
    for (const p of platforms) {
      const opts = targetsByPlatform[p] || [];
      if (opts.length > 1 && (selectedTargets[p] || []).length === 0) {
        const label = PLATFORMS.find((x) => x.key === p)?.label || p;
        toast.error(`Pick at least one ${label} page`);
        return false;
      }
    }
    if (postType === 'carousel' && media.filter((m) => m.type === 'image').length < 2) {
      toast.error('Carousel needs at least 2 images');
      return false;
    }
    if (postType === 'video_script' && !media.some((m) => m.type === 'video')) {
      toast.error('Video / Reel posts need a video file');
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    // Mode-specific validation
    let scheduledIso = null;
    if (mode === 'schedule') {
      if (!scheduledLocal) {
        toast.error('Pick a date and time');
        return;
      }
      // datetime-local is interpreted as the user's local timezone — convert
      // to ISO UTC for the API. Reject anything in the past so we don't
      // silently miss a publish window.
      const localDate = new Date(scheduledLocal);
      if (Number.isNaN(localDate.getTime())) {
        toast.error('Invalid date/time');
        return;
      }
      if (localDate.getTime() <= Date.now() + 30 * 1000) {
        toast.error('Scheduled time must be in the future');
        return;
      }
      scheduledIso = localDate.toISOString();
    }
    setSubmitting(true);
    try {
      // Build the chosen-destination map. Each platform fans out to every
      // selected page (one post row per page).
      const targetsPayload = {};
      platforms.forEach((plat) => {
        const opts = targetsByPlatform[plat] || [];
        const selIds = selectedTargets[plat] || [];
        const chosen = opts.filter((o) => selIds.includes(o.account_id));
        if (chosen.length) {
          targetsPayload[plat] = chosen.map((o) => ({
            account_id: o.account_id,
            account_name: o.account_name,
            kind: o.kind,
            client_id: o.client_id || null,
          }));
        }
      });
      const res = await api.post('/posts', {
        caption: caption.trim(),
        hashtags: hashtags.trim(),
        platforms,
        post_type: postType,
        media_items: media,
        targets: Object.keys(targetsPayload).length ? targetsPayload : undefined,
      });
      const ids = res.data.created_ids || [];
      if (ids.length === 0) {
        toast.error('No posts were created');
        return;
      }

      if (mode === 'draft') {
        toast.success(`Created ${ids.length} draft${ids.length === 1 ? '' : 's'}`);
        navigate('/posts');
        return;
      }

      // For now and schedule, we issue a follow-up call PER created post.
      // Run them in parallel so the spinner is short.
      if (mode === 'schedule') {
        await Promise.all(ids.map((id) =>
          api.post(`/posts/${id}/schedule`, { scheduled_for: scheduledIso })
        ));
        toast.success(`Scheduled ${ids.length} post${ids.length === 1 ? '' : 's'} for ${new Date(scheduledIso).toLocaleString()}`);
        navigate('/calendar');
        return;
      }

      if (mode === 'now') {
        // Backgrounded on the server, returns immediately with publishing status.
        const results = await Promise.allSettled(ids.map((id) =>
          api.post(`/posts/${id}/publish-now`)
        ));
        const ok = results.filter((r) => r.status === 'fulfilled').length;
        const failed = results.length - ok;
        if (failed === 0) {
          toast.success(`Publishing ${ok} post${ok === 1 ? '' : 's'} in the background — check My Posts in a moment.`);
        } else {
          toast.warning(`Queued ${ok}, ${failed} failed to start. See My Posts for details.`);
        }
        navigate('/posts');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const charCount = caption.length;
  const charLimit = platforms.includes('bluesky') ? 300 : 2200;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="compose-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900 flex items-center gap-2">
          <PenSquare className="w-6 h-6 text-indigo-600" />
          Compose post
        </h1>
        <p className="text-zinc-500 text-sm mt-0.5">
          Write a manual post — text, image, video or carousel — and publish to multiple platforms. No AI involved.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Post type selector */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Post type</CardTitle>
            </CardHeader>
            <CardContent className="grid sm:grid-cols-3 gap-2">
              {POST_TYPES.map((t) => {
                const Icon = t.icon;
                const active = postType === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setPostType(t.key)}
                    className={`text-left rounded border p-3 transition-colors ${
                      active
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-zinc-200 hover:border-zinc-300'
                    }`}
                    data-testid={`post-type-${t.key}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${active ? 'text-indigo-600' : 'text-zinc-500'}`} />
                      <p className="text-sm font-medium">{t.label}</p>
                    </div>
                    <p className="text-[11px] text-zinc-500 leading-snug">{t.hint}</p>
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Caption + hashtags */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span>Caption</span>
                <span className={`text-[11px] font-normal ${charCount > charLimit ? 'text-red-600' : 'text-zinc-400'}`}>
                  {charCount} / {charLimit}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Write your post here…"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={6}
                className="resize-y"
                data-testid="caption-textarea"
              />
              {!videoFeature.locked && caption.trim() && (
                <Button variant="outline" size="sm" className="text-xs text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  onClick={() => setVideoOpen(true)} data-testid="compose-record-teleprompter">
                  <ScrollText className="w-3.5 h-3.5 mr-1.5" /> Record video (teleprompter)
                </Button>
              )}
              <div className="space-y-1">
                <Label htmlFor="hashtags" className="text-xs flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  Hashtags
                </Label>
                <Input
                  id="hashtags"
                  placeholder="#marketing #brand #content"
                  value={hashtags}
                  onChange={(e) => setHashtags(e.target.value)}
                  onBlur={addHashtagFromInput}
                  data-testid="hashtags-input"
                />
                <p className="text-[10px] text-zinc-500">Space or comma-separated. Hash signs will be added automatically.</p>
              </div>
              <div className="rounded-lg border border-dashed border-zinc-200 p-3 space-y-2">
                <FunnelCtaPicker
                  value={ctaFunnel?.id || null}
                  onChange={setCtaFunnel}
                  refSource="compose"
                  label="Lead-capture CTA"
                />
                {ctaFunnel && (
                  <Button
                    type="button" variant="outline" size="sm"
                    data-testid="insert-cta-link-btn"
                    onClick={() => {
                      const url = buildTrackedUrl(ctaFunnel, 'compose');
                      if (caption.includes(url)) { toast.info('Link already in your post'); return; }
                      setCaption((c) => `${c}${c.trim() ? '\n\n' : ''}${url}`);
                      toast.success('Lead-capture link added to your post');
                    }}
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Insert link into post
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI image generation panel */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Wand2 className="w-4 h-4 text-indigo-500" />
                AI image
              </CardTitle>
              <CardDescription className="text-xs">
                Generate an image from your caption. Use <strong>Smart prompt</strong> to let the AI write a visual brief first, or paste your own.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                placeholder="Optional: describe the image (subject, mood, lighting). Leave blank to use your caption."
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={2}
                className="text-xs resize-none"
                data-testid="ai-prompt-textarea"
              />
              <div className="flex gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSmartPrompt}
                  disabled={smartLoading || !caption.trim()}
                  className="flex-1 text-xs"
                  data-testid="smart-prompt-btn"
                >
                  {smartLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <Sparkles className="w-3 h-3 mr-1" />
                  )}
                  Smart prompt
                </Button>
                <Button
                  size="sm"
                  onClick={handleGenerateImage}
                  disabled={genLoading || (!caption.trim() && !aiPrompt.trim())}
                  className="flex-1 text-xs bg-indigo-600 hover:bg-indigo-700"
                  data-testid="generate-image-btn"
                >
                  {genLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <ImageIcon className="w-3 h-3 mr-1" />
                  )}
                  Generate image
                </Button>
              </div>
              <p className="text-[10px] text-zinc-500">
                Tip: <strong>Smart prompt</strong> converts marketing copy into a concrete visual scene — much better image results.
              </p>
            </CardContent>
          </Card>

          {/* Media uploader */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-zinc-500" />
                  Media ({media.length})
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-media-btn"
                >
                  {uploading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  ) : (
                    <Upload className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Upload
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple={postType === 'carousel'}
                  onChange={handleUpload}
                  className="hidden"
                  data-testid="media-input"
                />
              </CardTitle>
              <CardDescription className="text-xs">
                {postType === 'carousel'
                  ? 'Carousel — pick 2 to 10 images.'
                  : postType === 'video_script'
                  ? 'Video / Reel — pick a single video file.'
                  : 'Optional: single image or video.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {media.length === 0 ? (
                <div className="border border-dashed border-zinc-200 rounded p-6 text-center">
                  <ImageIcon className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                  <p className="text-xs text-zinc-500">No media yet — upload to make this a visual post.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {media.map((m) => (
                    <div
                      key={m.id}
                      className="relative group aspect-square rounded border border-zinc-200 bg-zinc-50 overflow-hidden"
                      data-testid={`media-item-${m.id}`}
                    >
                      {m.type === 'image' ? (
                        <img src={m.url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-white">
                          <Video className="w-8 h-8 opacity-80" />
                        </div>
                      )}
                      <button
                        onClick={() => removeMedia(m.id)}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-media-${m.id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                        <span className="text-[9px] text-white/90 capitalize">{m.type}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right rail: platforms + actions */}
        <div className="space-y-4">
          <Card className="border-zinc-200 sticky top-4">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-zinc-500" />
                Platforms ({platforms.length})
              </CardTitle>
              <CardDescription className="text-xs">
                One draft is created per platform — you can fine-tune each one before scheduling.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="grid grid-cols-2 gap-1.5">
                {PLATFORMS.map((p) => {
                  const active = platforms.includes(p.key);
                  return (
                    <button
                      key={p.key}
                      onClick={() => togglePlatform(p.key)}
                      className={`text-xs px-2 py-1.5 rounded border flex items-center justify-between transition-colors ${
                        active ? p.cls : 'bg-white border-zinc-200 hover:border-zinc-300 text-zinc-700'
                      }`}
                      data-testid={`platform-${p.key}`}
                    >
                      <span className="font-medium">{p.label}</span>
                      {active && <Check className="w-3 h-3" />}
                    </button>
                  );
                })}
              </div>
              {platforms.length > 0 && (
                <div className="space-y-2 pt-2 border-t border-zinc-100" data-testid="target-selectors">
                  {platforms.map((p) => {
                    const opts = targetsByPlatform[p] || [];
                    const label = PLATFORMS.find((x) => x.key === p)?.label || p;
                    // 0 targets → platform not connected (drafts still allowed).
                    if (opts.length === 0) {
                      return (
                        <div key={p} className="flex items-center justify-between text-[11px] text-zinc-400">
                          <span>{label}</span>
                          <span className="italic">not connected</span>
                        </div>
                      );
                    }
                    // Exactly 1 target → no choice needed, just show it.
                    if (opts.length === 1) {
                      return (
                        <div key={p} className="flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                          <span className="font-medium text-zinc-700">{label}</span>
                          <span className="truncate max-w-[140px]" title={opts[0].account_name}>{opts[0].account_name}</span>
                        </div>
                      );
                    }
                    // Multiple targets → checkboxes to fan out to many pages.
                    const sel = selectedTargets[p] || [];
                    return (
                      <div key={p} className="space-y-1.5" data-testid={`target-group-${p}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-600">
                            <Building2 className="w-3 h-3" /> {label} — choose pages ({sel.length}/{opts.length})
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleAllTargets(p)}
                            className="text-[10px] text-indigo-600 hover:underline"
                            data-testid={`target-selectall-${p}`}
                          >
                            {allTargetsSelected(p) ? 'Clear' : 'Select all'}
                          </button>
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                          {opts.map((o) => (
                            <label
                              key={o.account_id}
                              className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer py-0.5"
                              data-testid={`target-option-${p}-${o.account_id}`}
                            >
                              <Checkbox
                                checked={sel.includes(o.account_id)}
                                onCheckedChange={() => toggleTarget(p, o.account_id)}
                                data-testid={`target-checkbox-${p}-${o.account_id}`}
                              />
                              <span className="truncate" title={o.account_name}>
                                {o.account_name}{o.client_name ? ` · ${o.client_name}` : ''}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="space-y-2 pt-3 border-t border-zinc-100">
                {/* Phase Q.1 — three publish modes */}
                <div className="grid grid-cols-3 gap-1" data-testid="publish-mode-group">
                  {[
                    { key: 'draft',    label: 'Draft',    icon: Save,  cls: 'border-zinc-300 text-zinc-700' },
                    { key: 'now',      label: 'Post now', icon: Send,  cls: 'border-emerald-400 text-emerald-700' },
                    { key: 'schedule', label: 'Schedule', icon: Clock, cls: 'border-sky-400 text-sky-700' },
                  ].map((m) => {
                    const Icon = m.icon;
                    const active = mode === m.key;
                    return (
                      <button
                        key={m.key}
                        onClick={() => setMode(m.key)}
                        className={`text-[11px] py-1.5 px-1 rounded border flex flex-col items-center gap-0.5 transition-colors ${
                          active ? `${m.cls} bg-white shadow-sm` : 'border-zinc-200 text-zinc-500 bg-zinc-50 hover:bg-white'
                        }`}
                        data-testid={`mode-${m.key}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {m.label}
                      </button>
                    );
                  })}
                </div>

                {mode === 'schedule' && (
                  <div className="space-y-1 pt-2" data-testid="schedule-picker">
                    <Label htmlFor="scheduled-at" className="text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Date &amp; time
                    </Label>
                    <Input
                      id="scheduled-at"
                      type="datetime-local"
                      value={scheduledLocal}
                      min={minScheduled}
                      onChange={(e) => setScheduledLocal(e.target.value)}
                      data-testid="scheduled-at-input"
                    />
                    <p className="text-[10px] text-zinc-500">
                      Time is in your local timezone. Posts publish automatically at this time.
                    </p>
                  </div>
                )}

                <Button
                  className={`w-full ${
                    mode === 'now'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : mode === 'schedule'
                      ? 'bg-sky-600 hover:bg-sky-700'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                  onClick={submit}
                  disabled={submitting}
                  data-testid="create-post-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  {!submitting && mode === 'now' && <Send className="w-4 h-4 mr-2" />}
                  {!submitting && mode === 'schedule' && <Clock className="w-4 h-4 mr-2" />}
                  {!submitting && mode === 'draft' && <Save className="w-4 h-4 mr-2" />}
                  {mode === 'draft'    && (totalPosts > 1 ? `Create ${totalPosts} drafts` : 'Create draft')}
                  {mode === 'now'      && (totalPosts > 1 ? `Post now (${totalPosts})` : 'Post now')}
                  {mode === 'schedule' && (totalPosts > 1 ? `Schedule ${totalPosts} posts` : 'Schedule post')}
                </Button>
                <p className="text-[10px] text-zinc-500 text-center">
                  {mode === 'draft' && <>Drafts open in <strong>My Posts</strong>.</>}
                  {mode === 'now'   && <>Publishes in the background — check <strong>My Posts</strong> in ~30s.</>}
                  {mode === 'schedule' && <>Auto-publishes at the chosen time — view in <strong>Calendar</strong>.</>}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <VideoStudioModal
        open={videoOpen}
        initialScript={`${caption || ''}${hashtags ? `\n\n${hashtags}` : ''}`.trim()}
        onClose={() => setVideoOpen(false)}
      />
    </div>
  );
};

export default ComposePage;
